import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../root/root-types';
import { ChannelDetails, ChannelTypeEnum, ChannelThread } from './model';
import { communicationApi } from './api';
import { isNull, isUndefined } from 'lodash';
import { IssueStatus } from '../thread/model';
import { MultiToastType } from '../utils/helpers/enum';

export const COMMUNICATION_FEATURE_KEY = 'commsData';

export interface ActionToastDataType {
  visibility: boolean;
  title: string;
  description: string;
  type: MultiToastType;
}

export interface ZoToastDataType {
  visibility: boolean;
  text: string;
  type?: 'success' | 'error';
}

export interface InviteChannelData {
  principalWorkspaceId: string;
  channelId: string;
  modalOpen: boolean;
}

export interface RecentThreadSearcList {
  channelId: string;
  firstDesc: string;
  secondDesc?: string;
  thirdDesc?: string;
}

export interface CommsState {
  channelGroups: string[];
  zoaiChannel: string;
  currentChannelId: string;
  actionToastData: ActionToastDataType;
  channelMemberRoleLoader: boolean;
  customerThreads: Record<string, ChannelDetails>;
  issueThreads: Record<string, ChannelDetails>;
  groupThreads: Record<string, ChannelDetails>;
  threadTabNavigationValue: ChannelTypeEnum;
  isSessionStarted: boolean;
  commonThreads: ChannelThread[];
  customersIssueThreadsMap: Record<string, Array<string>>;
  waConfigId: string;
  notFoundChannelId: string;
  showTypingIndicator: boolean;
  zoToastData: ZoToastDataType;
}

export const initialCommsState: CommsState = {
  channelGroups: [],
  zoaiChannel: '',
  currentChannelId: '',
  channelMemberRoleLoader: false,
  actionToastData: {
    visibility: false,
    title: '',
    description: '',
    type: MultiToastType.SUCCESS,
  },
  customerThreads: {},
  issueThreads: {},
  groupThreads: {},
  threadTabNavigationValue: ChannelTypeEnum.CUSTOMERTHREAD,
  isSessionStarted: false,
  commonThreads: [],
  customersIssueThreadsMap: {},
  waConfigId: '',
  notFoundChannelId: '',
  showTypingIndicator: false,
  zoToastData: {
    visibility: false,
    text: '',
    type: 'success',
  },
};

export const commsSlice = createSlice({
  name: COMMUNICATION_FEATURE_KEY,
  initialState: initialCommsState,
  reducers: {
    updateCurrentChannel(state, action: PayloadAction<string>) {
      state.currentChannelId = action.payload;
    },
    updateActionToastData(state, action: PayloadAction<ActionToastDataType>) {
      state.actionToastData = action.payload;
    },
    updateWaConfigId(state, action: PayloadAction<string>) {
      state.waConfigId = action.payload;
    },
    updateChannelMemberRoleLoader(state, action: PayloadAction<boolean>) {
      state.channelMemberRoleLoader = action.payload;
    },
    updateActionToastDataVisibility(state, action: PayloadAction<boolean>) {
      const tempData = state.actionToastData;
      state.actionToastData = { ...tempData, visibility: action.payload };
    },
    updateThreadTabNavigationValue(
      state,
      action: PayloadAction<ChannelTypeEnum>
    ) {
      state.threadTabNavigationValue = action.payload;
    },
    updateIsSessionStarted(state, action: PayloadAction<boolean>) {
      state.isSessionStarted = action.payload;
    },
    updateNotFoundChannelId(state, action: PayloadAction<string>) {
      state.notFoundChannelId = action.payload;
    },
    updateShowTypingIndicator(state, action: PayloadAction<boolean>) {
      state.showTypingIndicator = action.payload;
    },
    upsertChannel(
      state,
      action: PayloadAction<Record<string, Partial<ChannelDetails>>>
    ) {
      const data = action.payload;

      const stateMap = {
        ...state.customerThreads,
        ...state.issueThreads,
        ...state.groupThreads,
      };

      const getTargetState = (type: string, subType?: string) => {
        return type === ChannelTypeEnum.CUSTOMERTHREAD || subType === 'CUST'
          ? state.customerThreads
          : type === ChannelTypeEnum.ISSUETHREAD
            ? state.issueThreads
            : state.groupThreads;
      };

      for (const cId in data) {
        const oldData = { ...stateMap[cId] };
        const newData = data[cId];
        const updatedData = {
          ...(oldData || {}),
          ...newData,
          unreadCount: newData.unreadCount ?? oldData?.unreadCount ?? 0,
        };
        if (updatedData.channelType === 'C')
          updatedData.type = ChannelTypeEnum.CUSTOMERTHREAD;
        const { type, customerId = '', channelSubType } = updatedData;

        const targetState = getTargetState(type, channelSubType);

        if (
          oldData &&
          (oldData.type !== updatedData?.type ||
            oldData.channelSubType !== updatedData?.channelSubType)
        ) {
          const oldTarget = getTargetState(
            oldData.type,
            oldData.channelSubType
          );
          if (oldTarget[cId]) {
            delete oldTarget[cId];

            if (updatedData?.channelSubType === 'NONB') {
              return;
            }
          }
        }

        targetState[cId] = updatedData as ChannelDetails;

        if (type === ChannelTypeEnum.ISSUETHREAD && customerId) {
          const existingThreads =
            state.customersIssueThreadsMap[customerId] || [];
          const uniqueThreads = new Set(existingThreads);
          uniqueThreads.add(cId);
          state.customersIssueThreadsMap[customerId] =
            Array.from(uniqueThreads);
        }
      }
    },
    removeCustomerThreads(state) {
      state.customerThreads = {};
    },
    removeThread(state, action: PayloadAction<string>) {
      const channelId = action.payload;

      // Find the channel type in one pass
      const allThreads = {
        ...state.customerThreads,
        ...state.issueThreads,
        ...state.groupThreads,
      };

      const curr = allThreads[channelId];
      if (!curr) return; // If no channel found, early return

      const { type } = curr;
      let targetState;

      // Determine which state to remove the thread from based on the type
      switch (type) {
        case ChannelTypeEnum.CUSTOMERTHREAD:
          targetState = state.customerThreads;
          break;
        case ChannelTypeEnum.ISSUETHREAD:
          targetState = state.issueThreads;
          break;
        case ChannelTypeEnum.GROUPTHREAD:
          targetState = state.groupThreads;
          break;
        default:
          return; // If no matching type, do nothing
      }

      // Delete the channelId directly from the respective state
      delete targetState[channelId];

      // Update the currentChannelId to the first available channelId after removal
      const newChannelId = Object.keys(targetState)[0];
      state.currentChannelId = newChannelId || ''; // If no new ID, set as empty
    },
    clearAllChannelData(state) {
      state.currentChannelId = '';
      state.actionToastData = {
        visibility: false,
        title: '',
        description: '',
        type: MultiToastType.SUCCESS,
      };
      state.customerThreads = {};
      state.issueThreads = {};
      state.groupThreads = {};
    },
    allCommonTheads(state, action: PayloadAction<ChannelThread[]>) {
      state.commonThreads = action.payload;
    },
    updateChannelGroups(state, action: PayloadAction<string[]>) {
      state.channelGroups = action.payload;
    },
    updateZoaiChannel(state, action: PayloadAction<string>) {
      state.zoaiChannel = action.payload;
    },
    updateZoToastData(state, action: PayloadAction<ZoToastDataType>) {
      state.zoToastData = action.payload;
    },
    updateUnreadMessageCount(
      state,
      action: PayloadAction<Record<string, number>>
    ) {
      const payload = Object.entries(action.payload)[0];
      const channelId = payload[0];
      const count = payload[1];

      const maps = [
        state.customerThreads,
        state.issueThreads,
        state.groupThreads,
      ];

      for (const map of maps) {
        const channel = map[channelId];
        if (channel) {
          channel.unreadCount = count;
          break;
        }
      }
    },
    incrementUnreadMessageCount(state, action: PayloadAction<string>) {
      const channelId = action.payload;
      const maps = [
        state.customerThreads,
        state.issueThreads,
        state.groupThreads,
      ];

      for (const map of maps) {
        const channel = map[channelId];
        if (channel) {
          const old = channel.unreadCount ?? 0;
          channel.unreadCount = old + 1;
          break;
        }
      }
    },
    decrementUnreadMessageCount(state, action: PayloadAction<string>) {
      const channelId = action.payload;
      const maps = [
        state.customerThreads,
        state.issueThreads,
        state.groupThreads,
      ];

      for (const map of maps) {
        const channel = map[channelId];
        if (channel) {
          const old = channel.unreadCount ?? 0;
          channel.unreadCount = old - 1;
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      communicationApi.endpoints.getChannelDetails.matchFulfilled,
      (state, action) => {
        const channelData: Partial<ChannelDetails> = Object.fromEntries(
          Object.entries(action.payload).filter(
            ([_key, value]) => !isNull(value) && !isUndefined(value)
          )
        );
        const { id = '' } = channelData;

        const payload = {
          ...action,
          payload: { [id]: channelData },
        };
        commsSlice.caseReducers.upsertChannel(state, payload);
      }
    );
    builder.addMatcher(
      communicationApi.endpoints.leaveChannel.matchFulfilled,
      (state, action) => {
        const channelId = action.meta.arg.originalArgs.channelId;
        const payload = {
          ...action,
          payload: channelId,
        };
        commsSlice.caseReducers.removeThread(state, payload);
      }
    );
  },
});

export const {
  updateCurrentChannel,
  upsertChannel,
  updateActionToastData,
  updateActionToastDataVisibility,
  clearAllChannelData,
  updateChannelMemberRoleLoader,
  updateThreadTabNavigationValue,
  updateIsSessionStarted,
  allCommonTheads,
  updateWaConfigId,
  updateNotFoundChannelId,
  removeCustomerThreads,
  updateChannelGroups,
  updateZoaiChannel,
  updateShowTypingIndicator,
  updateZoToastData,
  updateUnreadMessageCount,
  incrementUnreadMessageCount,
  decrementUnreadMessageCount,
} = commsSlice.actions;

export const getCommsSliceState = (rootState: RootState): CommsState =>
  rootState[COMMUNICATION_FEATURE_KEY];

export const getCurrentChannelId = createSelector(
  getCommsSliceState,
  (state) => state.currentChannelId
);

export const getWaConfigId = createSelector(
  getCommsSliceState,
  (state) => state.waConfigId
);

export const getCurrentChannel = createSelector(getCommsSliceState, (state) =>
  state.currentChannelId !== ''
    ? {
        ...state.customerThreads,
        ...state.issueThreads,
        ...state.groupThreads,
      }[state.currentChannelId]
    : undefined
);

export const getActionToastData = createSelector(
  getCommsSliceState,
  (state) => state?.actionToastData
);

export const getChannelMemberRoleLoader = createSelector(
  getCommsSliceState,
  (state) => state?.channelMemberRoleLoader
);

export const getCustomerThreads = createSelector(getCommsSliceState, (state) =>
  Object.values(state.customerThreads)
);

export const getIssueThreads = createSelector(getCommsSliceState, (state) => {
  const threads = Object.values(state.issueThreads);
  const issues = threads.filter(
    (thread) =>
      thread.issueStatus !== IssueStatus.CLOSED || (thread.unreadCount ?? 0) > 0
  );
  return issues;
});

export const getClosedIssueThreads = createSelector(
  getCommsSliceState,
  (state) => {
    const threads = Object.values(state.issueThreads);
    const issues = threads.filter(
      (thread) => thread.issueStatus === IssueStatus.CLOSED
    );
    return issues;
  }
);

export const getAllIssueThreads = createSelector(
  getCommsSliceState,
  (state) => state.issueThreads
);

export const getGroupThreads = createSelector(getCommsSliceState, (state) =>
  Object.values(state.groupThreads)
);

export const getAllChannelsAndThreads = createSelector(
  getCommsSliceState,
  (state) => ({
    ...state.customerThreads,
    ...state.issueThreads,
    ...state.groupThreads,
  })
);

export const getThreadTabNavigationValue = createSelector(
  getCommsSliceState,
  (state) => state.threadTabNavigationValue
);

export const getIsSessionStarted = createSelector(
  getCommsSliceState,
  (state) => state.isSessionStarted
);

export const getZoeyChannel = createSelector(
  getCommsSliceState,
  (state) => state.zoaiChannel
);

export const getCommonThreads = createSelector(
  getCommsSliceState,
  (state) => state.commonThreads
);

export const getCustomerIssueThreadsMap = createSelector(
  getCommsSliceState,
  (state) => state.customersIssueThreadsMap
);

export const getUserChannelGroups = createSelector(
  getCommsSliceState,
  (state) => state.channelGroups
);

export const getShowTypingIndicator = createSelector(
  getCommsSliceState,
  (state) => state.showTypingIndicator
);

export const getZoToastData = createSelector(
  getCommsSliceState,
  (state) => state.zoToastData
);

export const getChannelById = (channelId: string) =>
  createSelector(
    getCommsSliceState,
    (state) =>
      ({
        ...state.customerThreads,
        ...state.issueThreads,
        ...state.groupThreads,
      })?.[channelId]
  );
